import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Card,
  Loading,
  Tag,
  Modal,
  Button,
  Toast,
  Input,
  Switch,
  List,
  TextArea,
  SearchBar,
} from 'antd-mobile';
import {
  FileOutline,
  EnvironmentOutline,
  ClockCircleOutline,
  RightOutline,
  SearchOutline,
} from 'antd-mobile-icons';
import dayjs from 'dayjs';
import { mobileApi } from '../api';

function formatDt(iso) {
  if (!iso) return null;
  return dayjs(iso).tz('Europe/Moscow').format('HH:mm');
}

const CANCEL_REASONS = [
  {
    label: '01А Невозможно провести отбор/измерения из-за отсутствия доступа к конструкции',
    value: '01А Невозможно провести отбор/измерения из-за отсутствия доступа к конструкции',
  },
  {
    label: '01Б Отсутствие заявленной конструкции или работ на объекте',
    value: '01Б Отсутствие заявленной конструкции или работ на объекте',
  },
  {
    label: '02А Отказ по указанию инспектора МГСН',
    value: '02А Отказ по указанию инспектора МГСН',
  },
  {
    label: '02Б Отказ по указанию руководства ГБУ',
    value: '02Б Отказ по указанию руководства ГБУ',
  },
  {
    label: '03 Отсутствие или поломка оборудования на месте отбора или в ГБУ',
    value: '03 Отсутствие или поломка оборудования на месте отбора или в ГБУ',
  },
  {
    label: '04 Не доехали до объекта',
    value: '04 Не доехали до объекта',
  },
  {
    label: '05 Замена одной работы другой по требованию инспектора',
    value: '05 Замена одной работы другой по требованию инспектора',
  },
  {
    label: '06 Замена одной работы другой из-за старого перечня',
    value: '06 Замена одной работы другой из-за старого перечня',
  },
  {
    label: '07 Дополнение работы в рамках существующей работы (вида работ/индекс)',
    value: '07 Дополнение работы в рамках существующей работы (вида работ/индекс)',
  },
  {
    label: '08 Отсутствие проектной документации',
    value: '08 Отсутствие проектной документации',
  },
];

function statusInfo(card) {
  if (card.arrived_office_at) {
    return { text: 'Прибыл в офис', color: 'success' };
  }
  if (card.departed_object_at) {
    return { text: 'Убыл с объекта', color: 'success' };
  }
  if (card.arrived_at_object_at) {
    return { text: 'Прибыл на объект', color: 'primary' };
  }
  if (card.departed_office_at) {
    return { text: 'Убыл из офиса', color: 'warning' };
  }
  if (card.confirmed_at) {
    return { text: 'Состав подтверждён', color: 'default' };
  }
  return { text: 'Состав не подтверждён', color: 'danger' };
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Геолокация не поддерживается'));
      return;
    }

    const tryGetPosition = (highAccuracy) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        (err) => {
          if (highAccuracy) {
            // Если точная геолокация не сработала (часто в помещении/машине),
            // пробуем ещё раз с меньшей точностью и кэшем.
            tryGetPosition(false);
          } else {
            let msg = 'Не удалось определить геолокацию';
            if (err.code === 1) {
              msg = 'Доступ к геолокации запрещён. Проверьте разрешение в настройках браузера/устройства.';
            } else if (err.code === 2) {
              msg = 'Геолокация недоступна. Включите GPS/местоположение и попробуйте на открытом пространстве.';
            } else if (err.code === 3) {
              msg = 'Превышено время ожидания геолокации. Попробуйте ещё раз на улице или с включённым GPS.';
            }
            reject(new Error(msg));
          }
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout: highAccuracy ? 10000 : 15000,
          maximumAge: highAccuracy ? 0 : 60000,
        }
      );
    };

    tryGetPosition(true);
  });
}

function GoszadaniePage() {
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const { data, isLoading, refetch } = useQuery(
    ['dispatchCards', today],
    () => mobileApi.dispatchCards(today)
  );

  const [detailCard, setDetailCard] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editableMembers, setEditableMembers] = useState([]);
  const [works, setWorks] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [replaceIdx, setReplaceIdx] = useState(null);
  const [replaceName, setReplaceName] = useState('');
  const [addMemberVisible, setAddMemberVisible] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newWork, setNewWork] = useState({ code: '', name: '' });
  const [workCodeOptions, setWorkCodeOptions] = useState([]);
  const [codePickerVisible, setCodePickerVisible] = useState(false);
  const [codeSearch, setCodeSearch] = useState('');
  const [reasonPickerVisible, setReasonPickerVisible] = useState(false);
  const [reasonPickerIdx, setReasonPickerIdx] = useState(null);
  const [worksSaving, setWorksSaving] = useState(false);
  const [addWorkSaving, setAddWorkSaving] = useState(false);

  const cards = data?.data || [];

  useEffect(() => {
    mobileApi
      .workCodes()
      .then((res) => setWorkCodeOptions(res.data || []))
      .catch(() => {
        setWorkCodeOptions([]);
        Toast.show({ icon: 'fail', content: 'Не удалось загрузить справочник кодов работ' });
      });
  }, []);

  useEffect(() => {
    if (codePickerVisible) {
      setCodeSearch('');
    }
  }, [codePickerVisible]);

  const openDetail = async (card) => {
    setDetailLoading(true);
    try {
      const res = await mobileApi.dispatchCard(card.id);
      const c = res.data;
      setDetailCard(c);
      setEditableMembers(c.confirmed_members?.length ? c.confirmed_members : (c.members || []));
      setWorks(c.works || []);
      setChecklist({
        inspector_arrival_time: c.checklist_inspector_arrival_time || '',
        inspector_departure_time: c.checklist_inspector_departure_time || '',
        inspector_arrived_before_group: !!c.checklist_inspector_arrived_before_group,
        inspector_left_after_group: !!c.checklist_inspector_left_after_group,
        risks_occurred: !!c.checklist_risks_occurred,
        risks_comment: c.checklist_risks_comment || '',
        notes: c.checklist_notes || '',
      });
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Не удалось загрузить карточку' });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailCard(null);
    setReplaceIdx(null);
    setAddMemberVisible(false);
    setNewMemberName('');
    setNewWork({ code: '', name: '' });
  };

  const refreshList = () => {
    refetch();
  };

  const handleConfirmMembers = async () => {
    if (!detailCard) return;
    try {
      const res = await mobileApi.confirmDispatchMembers(detailCard.id, editableMembers);
      setDetailCard(res.data);
      Toast.show({ icon: 'success', content: 'Состав подтверждён' });
      refreshList();
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка сохранения состава' });
    }
  };

  const buildGeoPayload = (geo) => ({ gps_lat: geo.lat, gps_lon: geo.lon });

  const handleDepartOffice = async () => {
    if (!detailCard) return;
    try {
      const geo = await getPosition();
      const res = await mobileApi.departOffice(detailCard.id, buildGeoPayload(geo));
      setDetailCard(res.data);
      Toast.show({ icon: 'success', content: 'Убытие из офиса зафиксировано' });
      refreshList();
    } catch (err) {
      Toast.show({
        icon: 'fail',
        content: err?.message || 'Не удалось определить геолокацию',
      });
    }
  };

  const handleArriveObject = async () => {
    if (!detailCard) return;
    try {
      const geo = await getPosition();
      const res = await mobileApi.arriveObject(detailCard.id, buildGeoPayload(geo));
      setDetailCard(res.data);
      Toast.show({ icon: 'success', content: 'Прибытие на объект зафиксировано' });
      refreshList();
    } catch (err) {
      Toast.show({
        icon: 'fail',
        content: err?.message || 'Не удалось определить геолокацию',
      });
    }
  };

  const handleDepartObject = async () => {
    if (!detailCard) return;
    try {
      const geo = await getPosition();
      const res = await mobileApi.departObject(detailCard.id, buildGeoPayload(geo));
      setDetailCard(res.data);
      Toast.show({ icon: 'success', content: 'Убытие с объекта зафиксировано' });
      refreshList();
    } catch (err) {
      Toast.show({
        icon: 'fail',
        content: err?.message || 'Не удалось определить геолокацию',
      });
    }
  };

  const handleArriveOffice = async () => {
    if (!detailCard) return;
    try {
      const geo = await getPosition();
      const res = await mobileApi.arriveOffice(detailCard.id, buildGeoPayload(geo));
      setDetailCard(res.data);
      Toast.show({ icon: 'success', content: 'Прибытие в офис зафиксировано' });
      refreshList();
    } catch (err) {
      Toast.show({
        icon: 'fail',
        content: err?.message || 'Не удалось определить геолокацию',
      });
    }
  };

  const handleSaveChecklist = async () => {
    if (!detailCard) return;
    const timeRe = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (
      !checklist.inspector_arrived_before_group &&
      checklist.inspector_arrival_time &&
      !timeRe.test(checklist.inspector_arrival_time)
    ) {
      Toast.show({ icon: 'fail', content: 'Время прибытия инспектора: ЧЧ:ММ' });
      return;
    }
    if (
      !checklist.inspector_left_after_group &&
      checklist.inspector_departure_time &&
      !timeRe.test(checklist.inspector_departure_time)
    ) {
      Toast.show({ icon: 'fail', content: 'Время убытия инспектора: ЧЧ:ММ' });
      return;
    }
    try {
      const res = await mobileApi.saveChecklist(detailCard.id, checklist);
      setDetailCard(res.data);
      Toast.show({ icon: 'success', content: 'Чек-лист сохранён' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Ошибка сохранения чек-листа';
      Toast.show({ icon: 'fail', content: String(detail) });
    }
  };

  const handleSaveWorks = async () => {
    if (!detailCard || worksSaving) return;
    setWorksSaving(true);
    try {
      const res = await mobileApi.saveWorks(detailCard.id, works);
      setDetailCard(res.data);
      setWorks(res.data.works || []);
      Toast.show({ icon: 'success', content: 'Работы сохранены' });
    } catch (err) {
      Toast.show({ icon: 'fail', content: 'Ошибка сохранения работ' });
    } finally {
      setWorksSaving(false);
    }
  };

  const handleAddWork = async () => {
    if (!detailCard || !newWork.code.trim() || addWorkSaving) {
      if (!addWorkSaving) Toast.show({ icon: 'fail', content: 'Не выбран код работы' });
      return;
    }
    setAddWorkSaving(true);
    try {
      const payload = {
        work_code: newWork.code.trim(),
        work_name: newWork.name.trim() || null,
      };
      const res = await mobileApi.addWork(detailCard.id, payload);
      // Сохраняем локально выставленные статусы работ, не перезаписывая их ответом сервера
      const added = {
        work_code: payload.work_code,
        work_name: payload.work_name || res.data?.works?.find((w) => w.work_code === payload.work_code)?.work_name || null,
        status: 'confirmed',
        reason: '',
      };
      const nextWorks = [...works, added];
      setWorks(nextWorks);
      setDetailCard((prev) => (prev ? { ...prev, works: nextWorks } : prev));
      setNewWork({ code: '', name: '' });
      Toast.show({ icon: 'success', content: 'Работа добавлена' });
    } catch (err) {
      console.error('addWork error', err);
      const detail = err?.response?.data?.detail || err?.message || 'Ошибка добавления работы';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setAddWorkSaving(false);
    }
  };

  const handleRemoveWork = async (idx) => {
    if (!detailCard || worksSaving) return;
    setWorksSaving(true);
    try {
      const res = await mobileApi.removeWork(detailCard.id, { index: idx });
      setDetailCard(res.data);
      setWorks(res.data.works || []);
      Toast.show({ icon: 'success', content: 'Работа удалена' });
    } catch (err) {
      console.error('removeWork error', err);
      const detail = err?.response?.data?.detail || err?.message || 'Ошибка удаления работы';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setWorksSaving(false);
    }
  };

  const removeMember = async (idx) => {
    const next = editableMembers.filter((_, i) => i !== idx);
    setEditableMembers(next);
    if (detailCard) {
      try {
        const res = await mobileApi.confirmDispatchMembers(detailCard.id, next);
        setDetailCard(res.data);
        Toast.show({ icon: 'success', content: 'Состав сохранён' });
      } catch (err) {
        Toast.show({ icon: 'fail', content: 'Ошибка сохранения состава' });
      }
    }
  };

  const startReplace = (idx) => {
    setReplaceIdx(idx);
    setReplaceName(editableMembers[idx]?.full_name || '');
  };

  const applyReplace = async () => {
    const name = replaceName.trim();
    if (!name) return;
    const next = editableMembers.map((m, i) =>
      i === replaceIdx ? { ...m, id: null, full_name: name } : m
    );
    setEditableMembers(next);
    setReplaceIdx(null);
    setReplaceName('');
    if (detailCard) {
      try {
        const res = await mobileApi.confirmDispatchMembers(detailCard.id, next);
        setDetailCard(res.data);
        Toast.show({ icon: 'success', content: 'Состав сохранён' });
      } catch (err) {
        Toast.show({ icon: 'fail', content: 'Ошибка сохранения состава' });
      }
    }
  };

  const addMember = async () => {
    const name = newMemberName.trim();
    if (!name) {
      setAddMemberVisible(false);
      return;
    }
    const next = [...editableMembers, { id: null, full_name: name }];
    setEditableMembers(next);
    setNewMemberName('');
    setAddMemberVisible(false);
    if (detailCard) {
      try {
        const res = await mobileApi.confirmDispatchMembers(detailCard.id, next);
        setDetailCard(res.data);
        Toast.show({ icon: 'success', content: 'Сотрудник добавлен' });
      } catch (err) {
        Toast.show({ icon: 'fail', content: 'Ошибка сохранения состава' });
      }
    }
  };

  const updateWorkStatus = (idx, status) => {
    if (worksSaving) return;
    setWorks((prev) =>
      prev.map((w, i) => (i === idx ? { ...w, status, reason: status === 'cancelled' ? w.reason : '' } : w))
    );
  };

  const updateWorkReason = (idx, reason) => {
    if (worksSaving) return;
    setWorks((prev) => prev.map((w, i) => (i === idx ? { ...w, reason } : w)));
  };

  const selectWorkCode = (codeItem) => {
    if (!codeItem || !codeItem.work_code) return;
    setNewWork((prev) => ({
      code: String(codeItem.work_code),
      name: codeItem.work_name || prev.name,
    }));
    setCodePickerVisible(false);
    setCodeSearch('');
  };

  const openReasonPicker = (idx) => {
    setReasonPickerIdx(idx);
    setReasonPickerVisible(true);
  };

  const selectReason = (reason) => {
    if (reasonPickerIdx !== null) {
      updateWorkReason(reasonPickerIdx, reason);
    }
    setReasonPickerVisible(false);
    setReasonPickerIdx(null);
  };

  const numericWorkCodeCompare = (a, b) => {
    const pa = String(a.work_code || '').split('.').map(Number);
    const pb = String(b.work_code || '').split('.').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const na = pa[i] || 0;
      const nb = pb[i] || 0;
      if (na !== nb) return na - nb;
    }
    return 0;
  };

  const filteredWorkCodes = useMemo(
    () =>
      workCodeOptions
        .filter((c) =>
          String(c.work_code || '')
            .toLowerCase()
            .includes(codeSearch.trim().toLowerCase())
        )
        .sort(numericWorkCodeCompare),
    [workCodeOptions, codeSearch]
  );

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Госзадание</h3>
      {isLoading ? (
        <Loading style={{ display: 'block', margin: '20px auto' }} />
      ) : cards.length === 0 ? (
        <Card style={{ textAlign: 'center', color: '#888' }}>
          Нет назначенных выездов на сегодня
        </Card>
      ) : (
        cards.map((card) => {
          const st = statusInfo(card);
          return (
            <Card
              key={card.id}
              style={{ marginBottom: 12 }}
              onClick={() => openDetail(card)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                  {card.address || 'Адрес не указан'}
                </div>
                <RightOutline style={{ color: '#999', marginLeft: 8 }} />
              </div>
              {card.case_number && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Дело {card.case_number}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#555', marginTop: 8 }}>
                {card.vehicle?.model || 'Авто не назначено'}
                {card.vehicle?.license_plate && ` · ${card.vehicle.license_plate}`}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Tag color={st.color}>{st.text}</Tag>
                {card.departure_time && (
                  <Tag color="default">
                    <ClockCircleOutline /> {card.departure_time}
                  </Tag>
                )}
                {card.members && (
                  <Tag color="default">{card.members.length} чел.</Tag>
                )}
              </div>
            </Card>
          );
        })
      )}

      {detailCard && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDetail();
          }}
        >
          <div
            style={{
              background: '#fff',
              height: '90vh',
              overflowY: 'auto',
              borderRadius: '16px 16px 0 0',
              padding: 12,
              paddingBottom: 24,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <h3 style={{ margin: 0, flex: 1, paddingRight: 12 }}>
                {detailCard.address || 'Карточка выезда'}
              </h3>
              <Button size="small" onClick={closeDetail}>Закрыть</Button>
            </div>

            {detailLoading ? (
              <Loading style={{ display: 'block', margin: '20px auto' }} />
            ) : (
              <>
                {detailCard.case_number && (
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                    {detailCard.case_number}
                  </div>
                )}

                {works.length > 0 && (
                  <Card style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Коды работ</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {works.map((w, idx) => {
                        const c =
                          w.status === 'confirmed'
                            ? { bg: '#d9f7be', color: '#389e0d', border: '#b7eb8f' }
                            : w.status === 'cancelled'
                            ? { bg: '#ffccc7', color: '#cf1322', border: '#ffa39e' }
                            : { bg: '#f5f5f5', color: '#595959', border: '#d9d9d9' };
                        return (
                          <span
                            key={idx}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '2px 8px',
                              borderRadius: 10,
                              fontSize: 13,
                              fontWeight: 500,
                              background: c.bg,
                              color: c.color,
                              border: `1px solid ${c.border}`,
                            }}
                          >
                            {w.work_code}
                          </span>
                        );
                      })}
                    </div>
                  </Card>
                )}

                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    <EnvironmentOutline /> Транспорт
                  </div>
                  <div>
                    {detailCard.vehicle?.model || 'Авто не назначено'}
                    {detailCard.vehicle?.license_plate && ` · ${detailCard.vehicle.license_plate}`}
                  </div>
                  <div style={{ marginTop: 4, color: '#666' }}>
                    Выезд: {detailCard.departure_time || '—'} · Возврат: {detailCard.return_time || '—'}
                  </div>
                </Card>

                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>
                    <FileOutline /> Состав группы
                  </div>
                  <List>
                    {editableMembers.map((m, idx) => (
                      <List.Item
                        key={idx}
                        extra={
                          <div style={{ display: 'flex', gap: 8 }}>
                            <Button size="mini" onClick={() => startReplace(idx)}>
                              Заменить
                            </Button>
                            <Button size="mini" color="danger" onClick={() => removeMember(idx)}>
                              Удалить
                            </Button>
                          </div>
                        }
                      >
                        {m.full_name}
                      </List.Item>
                    ))}
                  </List>
                  <Button
                    block
                    size="small"
                    style={{ marginTop: 8 }}
                    onClick={() => setAddMemberVisible(true)}
                  >
                    + Добавить сотрудника
                  </Button>
                  {!detailCard.departed_office_at && (
                    <Button
                      block
                      color="primary"
                      style={{ marginTop: 12 }}
                      onClick={handleConfirmMembers}
                    >
                      {detailCard.confirmed_at ? 'Сохранить состав' : 'Подтвердить состав'}
                    </Button>
                  )}
                </Card>

                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Маршрут на объекте</div>
                  {!detailCard.departed_office_at ? (
                    <>
                      {!detailCard.confirmed_at && (
                        <div style={{ marginBottom: 8, color: '#cf1322', fontSize: 13 }}>
                          Подтвердите состав группы, чтобы зафиксировать выезд из офиса
                        </div>
                      )}
                      <Button
                        block
                        color="primary"
                        disabled={!detailCard.confirmed_at}
                        style={{ marginBottom: 12 }}
                        onClick={handleDepartOffice}
                      >
                        Убыл из офиса
                      </Button>
                    </>
                  ) : (
                    <div style={{ marginBottom: 12, color: '#888', fontSize: 12 }}>
                      Убыл из офиса в {formatDt(detailCard.departed_office_at)}
                      {detailCard.departed_office_lat && (
                        <span>
                          {' '}
                          ({detailCard.departed_office_lat.toFixed(5)},{' '}
                          {detailCard.departed_office_lon.toFixed(5)})
                        </span>
                      )}
                    </div>
                  )}
                  <Button
                    block
                    color="primary"
                    disabled={!detailCard.departed_office_at || detailCard.arrived_at_object_at}
                    onClick={handleArriveObject}
                  >
                    Прибыл на объект
                  </Button>
                  {detailCard.arrived_at_object_at && (
                    <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                      Прибыл на объект в {formatDt(detailCard.arrived_at_object_at)}
                      {detailCard.arrived_at_object_lat && (
                        <span>
                          {' '}
                          ({detailCard.arrived_at_object_lat.toFixed(5)},{' '}
                          {detailCard.arrived_at_object_lon.toFixed(5)})
                        </span>
                      )}
                    </div>
                  )}
                  <Button
                    block
                    color="primary"
                    style={{ marginTop: 12 }}
                    disabled={!detailCard.arrived_at_object_at || detailCard.departed_object_at}
                    onClick={handleDepartObject}
                  >
                    Убыл с объекта
                  </Button>
                  {detailCard.departed_object_at && (
                    <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                      Убыл с объекта в {formatDt(detailCard.departed_object_at)}
                      {detailCard.departed_object_lat && (
                        <span>
                          {' '}
                          ({detailCard.departed_object_lat.toFixed(5)},{' '}
                          {detailCard.departed_object_lon.toFixed(5)})
                        </span>
                      )}
                    </div>
                  )}
                  <Button
                    block
                    color="primary"
                    style={{ marginTop: 12 }}
                    disabled={!detailCard.departed_object_at || detailCard.arrived_office_at}
                    onClick={handleArriveOffice}
                  >
                    Прибыл в офис
                  </Button>
                  {detailCard.arrived_office_at && (
                    <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                      Прибыл в офис в {formatDt(detailCard.arrived_office_at)}
                      {detailCard.arrived_office_lat && (
                        <span>
                          {' '}
                          ({detailCard.arrived_office_lat.toFixed(5)},{' '}
                          {detailCard.arrived_office_lon.toFixed(5)})
                        </span>
                      )}
                    </div>
                  )}
                </Card>

                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Чек-лист</div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                      Время прибытия инспектора
                    </div>
                    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Прибыл раньше группы экспертов</span>
                      <Switch
                        checked={checklist.inspector_arrived_before_group}
                        onChange={(v) =>
                          setChecklist((p) => ({
                            ...p,
                            inspector_arrived_before_group: v,
                            inspector_arrival_time: v ? '' : p.inspector_arrival_time,
                          }))
                        }
                      />
                    </div>
                    {!checklist.inspector_arrived_before_group && (
                      <Input
                        value={checklist.inspector_arrival_time}
                        onChange={(v) => setChecklist((p) => ({ ...p, inspector_arrival_time: v }))}
                        placeholder="ЧЧ:ММ"
                      />
                    )}
                    {checklist.inspector_arrived_before_group && (
                      <div style={{ fontSize: 12, color: '#888' }}>
                        Время прибытия не зафиксировано — инспектор прибыл раньше группы экспертов
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                      Время убытия инспектора
                    </div>
                    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Убыл позже группы экспертов</span>
                      <Switch
                        checked={checklist.inspector_left_after_group}
                        onChange={(v) =>
                          setChecklist((p) => ({
                            ...p,
                            inspector_left_after_group: v,
                            inspector_departure_time: v ? '' : p.inspector_departure_time,
                          }))
                        }
                      />
                    </div>
                    {!checklist.inspector_left_after_group && (
                      <Input
                        value={checklist.inspector_departure_time}
                        onChange={(v) => setChecklist((p) => ({ ...p, inspector_departure_time: v }))}
                        placeholder="ЧЧ:ММ"
                      />
                    )}
                    {checklist.inspector_left_after_group && (
                      <div style={{ fontSize: 12, color: '#888' }}>
                        Время убытия не зафиксировано — инспектор убыл позже группы экспертов
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span>Возникли риски</span>
                    <Switch
                      checked={checklist.risks_occurred}
                      onChange={(v) => setChecklist((p) => ({ ...p, risks_occurred: v }))}
                    />
                  </div>
                  {checklist.risks_occurred && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                        Комментарий по рискам
                      </div>
                      <TextArea
                        rows={2}
                        value={checklist.risks_comment}
                        onChange={(v) => setChecklist((p) => ({ ...p, risks_comment: v }))}
                        placeholder="Опишите риски"
                      />
                    </div>
                  )}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Примечания</div>
                    <TextArea
                      rows={2}
                      value={checklist.notes}
                      onChange={(v) => setChecklist((p) => ({ ...p, notes: v }))}
                      placeholder="Примечания"
                    />
                  </div>
                  <Button block color="primary" onClick={handleSaveChecklist}>
                    Сохранить чек-лист
                  </Button>
                </Card>

                <Card style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Коды работ</div>
                  <List>
                    {works.map((w, idx) => (
                      <List.Item
                        key={idx}
                        description={w.work_name || 'Название не указано'}
                        extra={
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Button
                              size="mini"
                              color={w.status === 'confirmed' ? 'success' : 'default'}
                              disabled={worksSaving}
                              onClick={() => updateWorkStatus(idx, 'confirmed')}
                            >
                              ✓
                            </Button>
                            <Button
                              size="mini"
                              color={w.status === 'cancelled' ? 'danger' : 'default'}
                              disabled={worksSaving}
                              onClick={() => updateWorkStatus(idx, 'cancelled')}
                            >
                              Отмена
                            </Button>
                            <Button
                              size="mini"
                              color="danger"
                              fill="outline"
                              disabled={worksSaving}
                              onClick={() => handleRemoveWork(idx)}
                            >
                              ×
                            </Button>
                          </div>
                        }
                      >
                        <div style={{ fontWeight: 500 }}>{w.work_code}</div>
                        <Tag color={w.status === 'confirmed' ? 'success' : w.status === 'cancelled' ? 'danger' : 'default'}>
                          {w.status === 'confirmed' ? 'Подтверждено' : w.status === 'cancelled' ? 'Отменено' : 'Запланировано'}
                        </Tag>
                        {w.status === 'cancelled' && (
                          <div
                            onClick={() => openReasonPicker(idx)}
                            style={{
                              marginTop: 4,
                              padding: '8px 12px',
                              border: '1px solid #d9d9d9',
                              borderRadius: 4,
                              fontSize: 13,
                              color: w.reason ? '#262626' : '#999',
                              background: '#fff',
                            }}
                          >
                            {w.reason || 'Выберите причину отмены'}
                          </div>
                        )}
                      </List.Item>
                    ))}
                  </List>
                  <Button block color="primary" style={{ marginTop: 8 }} onClick={handleSaveWorks} disabled={worksSaving} loading={worksSaving}>
                    Сохранить статусы работ
                  </Button>
                  <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 12 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Добавить работу</div>
                    <div style={{ marginBottom: 8 }}>
                      <Button
                        block
                        size="small"
                        onClick={() => {
                          console.log('open code picker', workCodeOptions.length);
                          Toast.show({ icon: 'success', content: `Загружено ${workCodeOptions.length} кодов` });
                          setCodePickerVisible(true);
                        }}
                      >
                        {newWork.code || 'Выбрать код работы'}
                      </Button>
                    </div>
                    <Input
                      placeholder="Название"
                      value={newWork.name}
                      onChange={(v) => setNewWork((p) => ({ ...p, name: v }))}
                    />
                    <Button block size="small" style={{ marginTop: 8 }} onClick={handleAddWork} disabled={addWorkSaving} loading={addWorkSaving}>
                      Добавить
                    </Button>
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      )}

      {replaceIdx !== null && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setReplaceIdx(null);
              setReplaceName('');
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px 12px 0 0',
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                paddingBottom: 12,
                borderBottom: '1px solid #eee',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Заменить сотрудника</span>
              <span
                onClick={() => {
                  setReplaceIdx(null);
                  setReplaceName('');
                }}
                style={{ color: '#1677ff', fontSize: 14, fontWeight: 400 }}
              >
                Закрыть
              </span>
            </div>
            <div style={{ padding: '12px 0' }}>
              <Input
                placeholder="ФИО замены"
                value={replaceName}
                onChange={setReplaceName}
                clearable
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                block
                onClick={() => {
                  setReplaceIdx(null);
                  setReplaceName('');
                }}
              >
                Отмена
              </Button>
              <Button
                block
                color="primary"
                onClick={() => {
                  applyReplace();
                  setReplaceName('');
                }}
              >
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      )}

      {addMemberVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setAddMemberVisible(false);
              setNewMemberName('');
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px 12px 0 0',
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                paddingBottom: 12,
                borderBottom: '1px solid #eee',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Добавить сотрудника</span>
              <span
                onClick={() => {
                  setAddMemberVisible(false);
                  setNewMemberName('');
                }}
                style={{ color: '#1677ff', fontSize: 14, fontWeight: 400 }}
              >
                Закрыть
              </span>
            </div>
            <div style={{ padding: '12px 0' }}>
              <Input
                placeholder="ФИО сотрудника"
                value={newMemberName}
                onChange={setNewMemberName}
                clearable
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button
                block
                onClick={() => {
                  setAddMemberVisible(false);
                  setNewMemberName('');
                }}
              >
                Отмена
              </Button>
              <Button block color="primary" onClick={addMember}>
                Добавить
              </Button>
            </div>
          </div>
        </div>
      )}

      {codePickerVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setCodePickerVisible(false);
              setCodeSearch('');
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px 12px 0 0',
              maxHeight: '70vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #eee',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Выбор кода работы</span>
              <span
                onClick={() => {
                  setCodePickerVisible(false);
                  setCodeSearch('');
                }}
                style={{ color: '#1677ff', fontSize: 14, fontWeight: 400 }}
              >
                Закрыть
              </span>
            </div>
            <div style={{ padding: 12, overflowY: 'auto' }}>
              <SearchBar
                placeholder="Поиск по коду"
                value={codeSearch}
                onChange={(v) => setCodeSearch(v)}
                style={{ marginBottom: 8 }}
              />
              {filteredWorkCodes.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 16 }}>Ничего не найдено</div>
              ) : (
                <div>
                  {filteredWorkCodes.map((c) => (
                    <div
                      key={c.work_code}
                      onClick={() => selectWorkCode(c)}
                      style={{
                        padding: '10px 0',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{c.work_code}</div>
                      {c.work_name && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                          {c.work_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {reasonPickerVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setReasonPickerVisible(false);
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px 12px 0 0',
              maxHeight: '60vh',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #eee',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Выбор причины отмены</span>
              <span
                onClick={() => setReasonPickerVisible(false)}
                style={{ color: '#1677ff', fontSize: 14, fontWeight: 400 }}
              >
                Закрыть
              </span>
            </div>
            <div style={{ padding: 12, overflowY: 'auto' }}>
              {CANCEL_REASONS.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 16 }}>Нет причин</div>
              ) : (
                <div>
                  {CANCEL_REASONS.map((r) => (
                    <div
                      key={r.value}
                      onClick={() => selectReason(r.value)}
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      {r.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoszadaniePage;
